<template>
  <div>
    <Connecting v-if="callState === 'connecting'" :licensePlate="licensePlate" :driver="driver" />
    <FailConnect
      v-if="callState === 'failConnect'"
      :licensePlate="licensePlate"
      :driver="driver"
      :vehicleId="vehicleId"
      @stopCall="stopCall"
      @tryAgain="startCall"
    />
    <OnGoing
      v-if="callState === 'onGoing'"
      :licensePlate="licensePlate"
      :driver="driver"
      :vehicleId="vehicleId"
      :muted="muted"
      :deviceType="deviceType"
      :audioLevel="audioLevel"
      @stopCall="stopCall"
      @muteDevice="muteDevice"
      @unmuteDevice="unmuteDevice"
    />
    <InOtherCall
      v-if="callState === 'inOtherCall'"
      :licensePlate="licensePlate"
      :driver="driver"
      :vehicleId="vehicleId"
      @stopCall="stopCall"
      @tryAgain="startCall"
    />
  </div>
</template>

<script>
import Connecting from './CallItemState/Connecting.vue'
import FailConnect from './CallItemState/FailConnect.vue'
import OnGoing from './CallItemState/OnGoing.vue'
import InOtherCall from './CallItemState/InOtherCall.vue'
import Voip from './lib/voip'
import { MixPanel } from '@/mixin/mixpanel'
import { mapGetters } from 'vuex'

export default {
  name: 'CallItem',
  components: {
    Connecting,
    FailConnect,
    OnGoing,
    InOtherCall,
  },
  mixins: [MixPanel],
  props: {
    licensePlate: {
      type: String,
      required: true,
    },
    driver: {
      type: String,
      required: false,
      default: '',
    },
    callState: {
      type: String, // ENUM: onGoing, connecting, failConnect, inOtherCall
      required: true,
    },
    vehicleId: {
      type: Number,
      required: true,
    },
    imei: {
      type: String,
      required: true,
    },
    token: {
      type: String,
      required: true,
    },
    muted: {
      type: Boolean,
      required: false,
      default: true,
    },
    deviceType: {
      type: String,
      required: true,
    },
    entryPoint: {
      type: String,
      required: false,
      default: '',
    },
    audioContext: {
      type: AudioContext,
      required: true,
    },
  },
  data() {
    return {
      voip: null,
      audioLevel: 0, // Audio level for wave animation (0-1)

      // state for vss
      recorderVss: null,
      audioPlayerVss: null,
    }
  },
  computed: {
    ...mapGetters({
      hasAccessAll: 'identity/hasAccessAll',
      getFeatureFlagValue: 'identity/getFeatureFlagValue',
    }),
  },
  watch: {
    muted: {
      handler(muted) {
        this.handleMute(muted)
      },
    },
  },
  async mounted() {
    await this.startCall()
  },

  async beforeDestroy() {
    if (this.deviceType === 'bsj') {
      if (this.voip) {
        this.voip.stopCapture()
      }
    } else if (this.deviceType === 'vss-howen') {
      this.stopCallVssHowen()
    }
  },

  methods: {
    callbackDeviceBuffering() {
      this.$emit('setCallsValue', this.vehicleId, 'state', 'connecting')
      this.$store.commit('voip/setVoipState', {
        vehicleId: this.vehicleId,
        values: { ...this.$store.state.voip.voipState[this.vehicleId], state: 'connecting' },
      })
    },
    callbackCallActiveAfterBuffering() {
      this.$emit('setCallsValue', this.vehicleId, 'state', 'onGoing')
      this.$store.commit('voip/setVoipState', {
        vehicleId: this.vehicleId,
        values: { ...this.$store.state.voip.voipState[this.vehicleId], state: 'onGoing' },
      })
    },
    callbackCallsOnGoing() {
      this.$emit('setCallsValue', this.vehicleId, 'state', 'onGoing')
      this.$store.commit('voip/setVoipState', {
        vehicleId: this.vehicleId,
        values: { ...this.$store.state.voip.voipState[this.vehicleId], state: 'onGoing' },
      })
    },
    callbackCallsFailed(reason) {
      if (reason === 'DAOC01') {
        this.$emit('setCallsValue', this.vehicleId, 'state', 'inOtherCall')
        this.$store.commit('voip/setVoipState', {
          vehicleId: this.vehicleId,
          values: { ...this.$store.state.voip.voipState[this.vehicleId], state: 'inOtherCall' },
        })
        this.$mcToast.error(this.$t('trackVideo.failedInOtherCall'))
      } else {
        this.$emit('setCallsValue', this.vehicleId, 'state', 'failConnect')
        this.$store.commit('voip/setVoipState', {
          vehicleId: this.vehicleId,
          values: { ...this.$store.state.voip.voipState[this.vehicleId], state: 'failConnect' },
        })
      }
    },
    callbackWsError() {
      this.$emit('setCallsValue', this.vehicleId, 'state', 'failConnect')
      this.$store.commit('voip/setVoipState', {
        vehicleId: this.vehicleId,
        values: { ...this.$store.state.voip.voipState[this.vehicleId], state: 'failConnect' },
      })
    },
    muteDevice() {
      this.track('VoIP Driver muted')
      this.$emit('setCallsValue', this.vehicleId, 'isMuted', true)
      this.voip.toggleMuteDevice(true)
      this.$forceUpdate()
    },
    unmuteDevice() {
      this.$emit('setCallsValue', this.vehicleId, 'isMuted', false)
      this.voip.toggleMuteDevice(false)
      this.$forceUpdate()
    },
    async startCall() {
      this.$emit('setCallsValue', this.vehicleId, 'state', 'connecting')
      if (this.deviceType === 'bsj') {
        this.voip = new Voip({
          token: this.token,
          vehicleId: this.vehicleId,
          audioContext: this.audioContext,
          bufferSize: this.getFeatureFlagValue('trackvision_voip_buffer_size'),
          noiseConcellationConfig: {
            thresholdStdDev: this.getFeatureFlagValue('trackvision_voip_threshold_stddev'),
            thresholdAmplitude: this.getFeatureFlagValue('trackvision_voip_threshold_amplitude'),
            thresholdRms: this.getFeatureFlagValue('trackvision_voip_threshold_rms'),
            hasScope: this.hasAccessAll(['b3o-trackvision-noise-cancellation:read']),
            isVehicleFilterNoiseConcellationActive: this.getFeatureFlagValue(
              'is_vehicle_filter_noise_cancellation'
            ),
            listVehicleIdNoiseCancellation:
              this.getFeatureFlagValue('list_vehicle_noise_cancellation') ?? [],
          },
        })

        await this.$nextTick()

        // Set up incoming audio level callback for wave animation (from server/driver)
        this.voip.onIncomingAudioLevel = (level) => {
          this.audioLevel = level
        }

        await this.$nextTick()
        await this.voip.startCapture({
          callbackState: {
            callbackCallActiveAfterBuffering: this.callbackCallActiveAfterBuffering,
            callbackDeviceBuffering: this.callbackDeviceBuffering,
            callbackCallsOnGoing: this.callbackCallsOnGoing,
            callbackCallsFailed: this.callbackCallsFailed,
            callbackWsError: this.callbackWsError,
          },
        })
        this.handleMute(this.muted)
        this.toggleMuteMic(this.$store.state.voip.isMuted)
      } else if (this.deviceType === 'vss-howen') {
        // eslint-disable-next-line no-undef
        HWRecorder.get(
          async (rec) => {
            this.recorderVss = rec
            await this.$nextTick()
            this.recorderVss.start()
          },
          {
            sampleBits: 16,
            sampleRate: 8000,
          },
          this.callbackAudioPut
        )
      }
    },
    stopCall() {
      const mapEntryPoint = {
        modalDetailVehicle: 'Modal Detail Vehicle',
        floatingMap: 'Floating Map',
        alertCenter: 'Alert Center',
        detailNotification: 'Detail Notification',
        controlTower: 'Control Tower',
        mediaPlayerStreaming: 'Media Player Streaming',
      }
      if (mapEntryPoint?.[this.entryPoint]) {
        this.track(`${mapEntryPoint[this.entryPoint]} - VoIP End Call`, {
          entryPoint: `${mapEntryPoint[this.entryPoint]}`,
        })
      }
      this.track('VoIP End Call')
      if (this.deviceType === 'bsj') {
        this.voip.stopCapture()
      } else if (this.deviceType === 'vss-howen') {
        this.stopCallVssHowen()
      }
      this.$store.dispatch('voip/endCall', { vehicleId: this.vehicleId })
    },
    toggleMuteMic(isMuted) {
      if (this.voip) {
        this.voip.mediaStream.getAudioTracks().forEach((track) => {
          if (isMuted) {
            track.enabled = false
          } else {
            track.enabled = true
          }
        })
      }
    },
    async callbackAudioPut(arrayOfPcmData) {
      if (this.audioPlayerVss == null) {
        const wsUrl = process.env.VUE_APP_VSS_WS_URL
        const port = process.env.VUE_APP_VSS_PORT
        const serverIp = '52.220.96.82' // TODO : Put this to env
        const wsFullUrl = wsUrl + ':' + port + '/stream?ipaddr=' + serverIp

        const gUserName = this.$store.state.identity.whoami?.email
        const gFuncCode = '3'
        const sessionId = Date.now()

        const jsonRequest =
          '{"action":"3001","payload":{"sessionID":"' +
          sessionId +
          '","deviceID":"' +
          this.imei +
          '","channel":"1","workMode":"1","username":"' +
          gUserName +
          '","bussType":"' +
          gFuncCode +
          '"}}'
        // eslint-disable-next-line no-undef
        this.audioPlayerVss = new HWMediaPlayer(wsFullUrl, jsonRequest)
        await this.$nextTick()
        this.audioPlayerVss.setSessionStateCallback(this.callbackSessionStateChangeVss)
        this.audioPlayerVss.play()
      }
      this.audioPlayerVss.putInAudioPcm(arrayOfPcmData, this.$store.state.voip.isMuted)
    },
    callbackSessionStateChangeVss(stateObj) {
      const audioPlayerStateIdle = 0
      const audioPlayerStatePlaying = 1
      const audioPlayerStatePausing = 2
      const audioPlayerStateCalling = 3
      const audioPlayerStateEnd = -1

      switch (stateObj.state) {
        case audioPlayerStatePlaying:
          this.$emit('setCallsValue', this.vehicleId, 'state', 'onGoing')
          break
        case audioPlayerStateCalling:
          this.$emit('setCallsValue', this.vehicleId, 'state', 'connecting')
          break
        case audioPlayerStateEnd:
          this.stopCallVssHowen()
          break
        default:
          break
      }
    },

    stopCallVssHowen() {
      if (this.audioPlayerVss) {
        this.audioPlayerVss.stop()
        this.audioPlayerVss = null
      }
      if (this.recorderVss) this.recorderVss.stop()
    },
    handleMute(muted) {
      if (this.voip) {
        if (muted) {
          this.voip.toggleMuteDevice(true)
        } else {
          this.voip.toggleMuteDevice(false)
        }
        this.$forceUpdate()
      }
    },
  },
}
</script>
